import {Component, Input, OnInit} from '@angular/core';
import {AbstractControl, UntypedFormControl} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {
  REF_MODEL_STORE,
  RefModelNode,
  RefModelStoreService,
  TakeUntilComponentDestroy,
  constants
} from '@q9elements/ui-kit/common';
import {
  AvailableOption,
  CnaDependenciesMediatorService,
  CustomNodeAttributeInterface,
  DependencyInstance,
  FIELD_TYPES
} from '@q9elements/ui-kit/refmodels';

@Component({
  selector: 'custom-node-attribute',
  templateUrl: './custom-node-attribute.component.html',
  styleUrls: ['./custom-node-attribute.component.scss']
})
export class CustomNodeAttributeComponent extends TakeUntilComponentDestroy implements OnInit {
  @Input() customNodeAttribute: CustomNodeAttributeInterface;
  @Input() control: (UntypedFormControl | AbstractControl) & {isPredefined: boolean};
  @Input() disabled: boolean;
  @Input() position: number;

  public customNodeAttributeValue: string | undefined;
  public customNodeAttributeTypesMap = constants.customNodeAttributeTypesMap;
  public availableListOptions: AvailableOption[] = [];
  public dependencyInstances: DependencyInstance[] = [];

  private selectedNode: RefModelNode;
  private isFirstChange = true;

  get isPredefinedOption() {
    return this.control.isPredefined;
  }

  constructor(
    private rmeStore: RefModelStoreService,
    private cnaDependencyService: CnaDependenciesMediatorService,
    private translate: TranslateService
  ) {
    super();
  }

  ngOnInit() {
    this.subscribeToSelectedNode();
    this.controlSetup();
  }

  private controlSetup() {
    if (this.disabled) {
      this.control.disable();
    }

    const attributeType = this.customNodeAttribute?.properties?.type;
    if (attributeType === FIELD_TYPES.LIST || attributeType === FIELD_TYPES.MULTI_PICKLIST) {
      this.subscribeToSelectedChanges();
      this.subscribeToAvailableOptions();
      this.dependencyInstances = this.cnaDependencyService.getDependenciesInstances(
        this.customNodeAttribute._id
      );
    }

    if (attributeType === FIELD_TYPES.DATE_TIME) {
      this.subscribeToDateChanges();
    }
  }

  private subscribeToSelectedChanges() {
    const currentValueId = this.control.value;
    if (currentValueId) {
      this.cnaDependencyService.changeAvailableFields(this.customNodeAttribute._id, currentValueId);
    }

    this.control.valueChanges.pipe(this.takeUntilComponentDestroy()).subscribe(newValueId => {
      if (!newValueId) return;
      const attributeType = this.customNodeAttribute?.properties?.type;
      if (attributeType === FIELD_TYPES.MULTI_PICKLIST) {
        const selectedHiddenValues = newValueId.filter((value: string) => {
          return this.availableListOptions.find(option => option._id === value)?.hidden;
        });
        if (selectedHiddenValues.length) {
          const newValuesNoHidden = newValueId.filter(
            (value: string) => !selectedHiddenValues.includes(value)
          );
          this.control.setValue(newValuesNoHidden);
          this.cnaDependencyService.changeAvailableFields(
            this.customNodeAttribute._id,
            newValuesNoHidden
          );
          return;
        }
      }

      this.cnaDependencyService.changeAvailableFields(this.customNodeAttribute._id, newValueId);
    });
  }

  private subscribeToAvailableOptions() {
    this.cnaDependencyService
      .getAvailableFields(this.customNodeAttribute._id)
      .pipe(this.takeUntilComponentDestroy())
      .subscribe((availableValues: string[] | 'all') => {
        if (availableValues === 'all') {
          this.availableListOptions = this.customNodeAttribute.properties.options.slice();
          return;
        }
        this.defineAvailableOptions(availableValues);
        const hasValue = this.control?.value || this.control?.value?.length;
        const shouldValidateValue = this.isPredefinedOption || !this.isFirstChange;
        const shouldTryToAddOption = this.isFirstChange && !this.isPredefinedOption;

        if (shouldTryToAddOption && hasValue) {
          this.addSelectedValueToListOfAvailableOptions();
        }

        this.isFirstChange = false;
        if (hasValue && shouldValidateValue) {
          this.validateSelectedOption(availableValues);
        }
      });
  }

  private addSelectedValueToListOfAvailableOptions() {
    if (this.customNodeAttribute.properties.type === this.customNodeAttributeTypesMap.LIST) {
      const selectedValue = this.customNodeAttribute.properties.options.find(
        option => option._id === this.control.value
      );

      if (selectedValue) {
        const valueAlreadyInList = this.availableListOptions.some(
          option => option._id === selectedValue._id
        );

        if (!valueAlreadyInList) {
          this.availableListOptions.push({...selectedValue, hidden: true});
        }
      }
    }

    if (
      this.customNodeAttribute.properties.type === this.customNodeAttributeTypesMap.MULTI_PICKLIST
    ) {
      const selectedValues = Boolean(this.control.value.length) && this.control.value;
      if (selectedValues) {
        const valuesToAdd = selectedValues
          .filter(
            (value: string) => !this.availableListOptions.some(option => option._id === value)
          )
          .map((value: string) =>
            this.customNodeAttribute.properties.options.find(option => option._id === value)
          )
          .map((option: AvailableOption) => ({...option, hidden: true}));

        this.availableListOptions = [...this.availableListOptions, ...valuesToAdd];
      }
    }
  }

  private validateSelectedOption(availableValues: string[]) {
    if (this.customNodeAttribute.properties.type === this.customNodeAttributeTypesMap.LIST) {
      if (!availableValues.includes(this.control.value)) {
        this.control.setValue(null);
      }
    }

    if (
      this.customNodeAttribute.properties.type === this.customNodeAttributeTypesMap.MULTI_PICKLIST
    ) {
      const selectedValues = this.control.value;
      if (selectedValues) {
        const invalidValues = selectedValues.filter(
          (value: string) => !availableValues.includes(value)
        );
        if (invalidValues.length) {
          this.control.setValue(
            selectedValues.filter((value: string) => !invalidValues.includes(value))
          );
        }
      }
    }
  }

  private defineAvailableOptions(newOptions: string[]) {
    this.availableListOptions = this.customNodeAttribute.properties.options.filter(option => {
      return newOptions.includes(option._id);
    });
  }

  private getCustomNodeAttributeValue(attribute: CustomNodeAttributeInterface) {
    this.customNodeAttributeValue = this.selectedNode.customNodeAttributes?.find(
      cna => cna.field === attribute._id
    )?.value;
  }

  private subscribeToSelectedNode() {
    this.rmeStore
      .select(REF_MODEL_STORE.SELECTED_NODE)
      .pipe(this.takeUntilComponentDestroy())
      .subscribe((node: RefModelNode) => {
        this.selectedNode = node;
        this.getCustomNodeAttributeValue(this.customNodeAttribute);
      });
  }

  private subscribeToDateChanges() {
    this.control.valueChanges.pipe(this.takeUntilComponentDestroy()).subscribe(() => {
      if (this.control.touched) {
        this.control.markAsDirty();
      }
    });
  }

  private isNoAvailableOptions() {
    return (
      !this.availableListOptions.length || this.availableListOptions.every(option => option.hidden)
    );
  }

  public getHintMessage(): string | null {
    if (
      !this.customNodeAttributeValue &&
      this.isPredefinedOption &&
      !this.control.hasError('required') &&
      this.availableListOptions.length
    ) {
      return this.translate.instant('SIDEBAR.CNA_PREDEFINED_VALUE_HINT');
    } else if (this.isNoAvailableOptions()) {
      return this.translate.instant('SIDEBAR.NO_AVAILABLE_OPTIONS');
    }
    return null;
  }
}
